import {
    Box,
    Slider,
    Typography
} from '@mui/material';

import {
    useEffect,
    useMemo
} from 'react';

import { useMapStore } from '../../stores/map/useMapStore';
import { usePezinhoSocket } from '../../hooks/map/usePezinhoSocket';

import {
    m,
    formatDate,
    formatHora
} from '../../utils/dateUtils';

const LINE_DEFAULT_RANGE_HOURS = 1;
const LINE_MAX_RANGE_HOURS = 24;

function formatRangeDateTime(value) {

    if (!value) {
        return '';
    }

    return (
        `${formatDate(value)} ` +
        `${formatHora(value, 'HH:mm')}`
    );
}

export default function MapPezinhoRange() {

    const modeMap = useMapStore(
        state => state.modeMap
    );

    const tripData = useMapStore(
        state => state.tripData
    );

    const startAt = useMapStore(
        state => state.pezinho.startAt
    );

    const endAt = useMapStore(
        state => state.pezinho.endAt
    );

    const setPezinhoPeriod = useMapStore(
        state => state.setPezinhoPeriod
    );

    const loading = useMapStore(
        state => state.pezinho.loading
    );

    const {
        searchPezinho
    } = usePezinhoSocket();

    // =========================
    // RANGE
    // =========================

    const range = useMemo(() => {

        if (modeMap === 'line') {

            const max = m();

            const min = max
                .clone()
                .subtract(
                    LINE_MAX_RANGE_HOURS,
                    'hours'
                );

            const defaultStart = max
                .clone()
                .subtract(
                    LINE_DEFAULT_RANGE_HOURS,
                    'hours'
                );

            return {
                min,
                max,
                defaultStart,
                defaultEnd: max.clone()
            };
        }

        if (
            modeMap === 'trip' &&
            tripData?.startAt &&
            tripData?.endAt
        ) {

            const defaultStart =
                m(tripData.startAt);

            const defaultEnd =
                m(tripData.endAt);

            if (
                !defaultStart.isValid() ||
                !defaultEnd.isValid()
            ) {
                return null;
            }

            return {
                min: defaultStart
                    .clone()
                    .subtract(
                        30,
                        'minutes'
                    ),

                max: defaultEnd
                    .clone()
                    .add(
                        30,
                        'minutes'
                    ),

                defaultStart:
                    defaultStart.clone(),

                defaultEnd:
                    defaultEnd.clone()
            };
        }

        return null;

    }, [
        modeMap,
        tripData
    ]);

    // =========================
    // TOTAL MINUTOS
    // =========================

    const totalMinutes = useMemo(() => {

        if (!range) {
            return 0;
        }

        return range.max.diff(
            range.min,
            'minutes'
        );

    }, [range]);

    const defaultRangePercent = useMemo(() => {

        if (
            !range ||
            modeMap !== 'trip' ||
            totalMinutes <= 0
        ) {
            return null;
        }

        const startMinutes =
            range.defaultStart.diff(
                range.min,
                'minutes'
            );

        const endMinutes =
            range.defaultEnd.diff(
                range.min,
                'minutes'
            );

        return {
            start:
                (
                    startMinutes /
                    totalMinutes
                ) * 100,

            end:
                (
                    endMinutes /
                    totalMinutes
                ) * 100
        };

    }, [
        range,
        modeMap,
        totalMinutes
    ]);

    // =========================
    // INICIALIZA
    // =========================

    useEffect(() => {

        if (
            !range ||
            startAt ||
            endAt
        ) {
            return;
        }

        setPezinhoPeriod({
            startAt:
                range.defaultStart.toISOString(),

            endAt:
                range.defaultEnd.toISOString()
        });

        searchPezinho();

    }, [
        range,
        startAt,
        endAt,
        setPezinhoPeriod,
        searchPezinho
    ]);

    // =========================
    // SLIDER VALUE
    // =========================

    const sliderValue = useMemo(() => {

        if (!range) {
            return [0, 0];
        }

        const currentStart =
            startAt
                ? m(startAt)
                : range.defaultStart;

        const currentEnd =
            endAt
                ? m(endAt)
                : range.defaultEnd;

        return [
            currentStart.diff(
                range.min,
                'minutes'
            ),

            currentEnd.diff(
                range.min,
                'minutes'
            )
        ];

    }, [
        range,
        startAt,
        endAt
    ]);

    if (
        !range ||
        totalMinutes <= 0
    ) {
        return null;
    }

    // =========================
    // CHANGE
    // =========================

    const handleChange = (_, value) => {

        const [
            startMinutes,
            endMinutes
        ] = value;

        setPezinhoPeriod({

            startAt: range.min
                .clone()
                .add(
                    startMinutes,
                    'minutes'
                )
                .toISOString(),

            endAt: range.min
                .clone()
                .add(
                    endMinutes,
                    'minutes'
                )
                .toISOString()

        });

    };

    return (
        <Box
            sx={(theme) => ({
                width: '100%',
                boxSizing: 'border-box',

                px: 1.5,
                py: 0.5,

                borderRadius: 1,

                backgroundColor:
                    theme.palette.background.paper,

                boxShadow: theme.shadows[2]
            })}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        whiteSpace: 'nowrap',
                        lineHeight: 1
                    }}
                >
                    {formatRangeDateTime(
                        startAt ||
                        range.defaultStart
                    )}
                </Typography>

                <Slider
                    value={sliderValue}
                    min={0}
                    max={totalMinutes}
                    step={1}
                    onChange={handleChange}
                    onChangeCommitted={() => {
                        searchPezinho();
                    }}
                    disabled={loading}
                    disableSwap
                    size="small"
                    sx={(theme) => ({
                        mx: 0.5,

                        ...(defaultRangePercent && {

                            '& .MuiSlider-rail': {
                                opacity: 1,

                                background: `
                                    linear-gradient(
                                        to right,

                                        ${theme.palette.action.disabledBackground}
                                            0%,

                                        ${theme.palette.action.disabledBackground}
                                            ${defaultRangePercent.start}%,

                                        ${theme.palette.primary.light}
                                            ${defaultRangePercent.start}%,

                                        ${theme.palette.primary.light}
                                            ${defaultRangePercent.end}%,

                                        ${theme.palette.action.disabledBackground}
                                            ${defaultRangePercent.end}%,

                                        ${theme.palette.action.disabledBackground}
                                            100%
                                    )
                                `
                            }

                        })
                    })}
                />

                <Typography
                    variant="caption"
                    sx={{
                        whiteSpace: 'nowrap',
                        lineHeight: 1
                    }}
                >
                    {formatRangeDateTime(
                        endAt ||
                        range.defaultEnd
                    )}
                </Typography>
            </Box>
        </Box>
    );
}